import { Dropdown, DropDownButton } from '@/shared/components/Dropdown';
import { Textarea, TextField } from '@/shared/components/TextField';
import { OfferInfiniteDropdown } from '@/epics/subscriptionManagment/components/OfferInfiniteDropdown';
import CreateLayout from '@/shared/layout/createLayout';
import { useMemo } from 'react';
import Loader from '@/app/routes/Loader';
import { ToggleButton } from '@/shared/components/ToggleButton';
import { Snackbar } from '@/shared/components/Snackbar';
import { UpdateSubscriptionStatusDialog } from '../subscriptionList/UpdateSubscriptionStatusDialog';
import {
  useEditSubscription,
  type EditSubscriptionFormProps,
} from '@/epics/subscriptionManagment/hooks/useEditSubscription';
import {
  PERIOD_TYPE_OPTIONS,
  PERIOD_OPTIONS_MONTHLY,
  PERIOD_OPTIONS_YEARLY,
  MAX_PRICE,
} from '@/epics/subscriptionManagment/hooks/useCreateSubscription';

const EditSubscription = () => {
  const {
    isLoading,
    formData,
    setFormData,
    durationType,
    setDurationType,
    period,
    setPeriod,
    cycle,
    setCycle,
    cycleOptions,
    errors,
    setErrors,
    isSubmitting,
    isSnackbarOpen,
    snackbarError,
    isCancelModalOpen,
    handleSubmit,
    handleCancel,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleCloseSnackbar,
    formatPriceWithSpaces,
    clampPriceToMax,
    getBillingDescription,
    modifiedBy,
    modifiedDate,
    hasChanges,
    isPreviewStep,
    status,
    handleStatusChange,
    isStatusDialogOpen,
    handleCloseStatusDialog,
    handleConfirmStatusUpdate,
    isStatusSubmitting,
    showStatusSuccessSnackbar,
    statusSuccessMessage,
    handleCloseStatusSuccessSnackbar,
    showStatusErrorSnackbar,
    statusErrorMessage,
    handleCloseStatusErrorSnackbar,
    selectedOffer,
    setSelectedOffer,
  } = useEditSubscription();

  const EditSubscriptionHeader = () => {
    return (
      <div className="flex items-center justify-between">
        <p className="text-titleLarge text-neutral-10">
          {isPreviewStep ? 'Podgląd planu' : 'Edytuj plan'}
        </p>
        <div className="flex items-center gap-2">
          <p className="text-[12px] text-on-surface-variant">Status:</p>
          <div
            onClick={(e) => {
              if (isSubmitting || isLoading || isStatusSubmitting) {
                return;
              }
              e.stopPropagation();
              handleStatusChange();
            }}
            className={`flex min-w-[110px] items-center ${
              isSubmitting || isLoading || isStatusSubmitting
                ? 'cursor-not-allowed'
                : 'cursor-pointer'
            }`}
          >
            <div className="leading-none pointer-events-none">
              <ToggleButton
                checked={status === 'active'}
                onChange={() => {
                  // Prevent toggle from changing - only open dialog
                }}
                label={status === 'active' ? 'Aktywny' : 'Nieaktywny'}
                size="XSmall"
                disabled={isSubmitting || isLoading || isStatusSubmitting}
                data-testid="toggle-status"
              />
            </div>
          </div>
        </div>
      </div>
    );
  };
  const EditSubscriptionForm = useMemo(
    () =>
      function EditSubscriptionForm({
        formData,
        setFormData,
        durationType,
        setDurationType,
        period,
        setPeriod,
        cycle,
        setCycle,
        cycleOptions,
        errors,
        setErrors,
        formatPriceWithSpaces,
        clampPriceToMax,
        getBillingDescription,
        isSubmitting,
        isPreviewStep,
        modifiedBy,
        modifiedDate,
        selectedOffer,
        setSelectedOffer,
      }: EditSubscriptionFormProps) {
        return (
          <>
            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">
                Szczegóły planu
              </p>
              <div className="flex flex-col gap-8">
                <div className="flex gap-4 ">
                  <TextField
                    type="text"
                    inputMode="text"
                    label="Nazwa planu"
                    maxLength={200}
                    width={'50%'}
                    autoFocus={true}
                    value={formData.planName}
                    onChange={(e) => {
                      if (isSubmitting) {
                        return;
                      }
                      if (errors.planName) {
                        setErrors((prev) => ({
                          ...prev,
                          planName: '',
                        }));
                      }
                      const next = e.target.value.trimStart().slice(0, 200);
                      setFormData((prev) => ({
                        ...prev,
                        planName: next,
                      }));
                    }}
                    error={!!errors.planName}
                    errorMessage={errors.planName ?? ''}
                    disabled={isSubmitting}
                    readOnly={isPreviewStep}
                  />
                </div>
                <Textarea
                  error={!!errors.description}
                  errorMessage={errors.description ?? ''}
                  label="Opis"
                  maxLength={500}
                  fullWidth
                  rows={4}
                  value={formData.description}
                  onChange={(e) => {
                    if (isSubmitting) {
                      return;
                    }
                    if (errors.description) {
                      setErrors((prev) => ({
                        ...prev,
                        description: '',
                      }));
                    }
                    setFormData((prev) => ({
                      ...prev,
                      description:
                        e.target.value.trimStart().slice(0, 500) || '',
                    }));
                  }}
                  disabled={isSubmitting}
                  readOnly={isPreviewStep}
                />
                <div className="flex gap-4">
                  <div style={{ flex: '1 1 0', minWidth: 0 }}>
                    <TextField
                      fullWidth={true}
                      label="Zmodyfikowane przez"
                      maxLength={200}
                      value={modifiedBy}
                      disabled={true}
                    />
                  </div>
                  <div style={{ flex: '1 1 0', minWidth: 0 }}>
                    <TextField
                      fullWidth={true}
                      label="Data modyfikacji"
                      maxLength={200}
                      value={modifiedDate}
                      disabled={true}
                    />
                  </div>
                  <div style={{ flex: '1 1 0', minWidth: 0 }} />
                </div>
              </div>
            </div>

            <div className="border-b-2 border-[#DCDDE0]" />

            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">
                Okres i rozliczenia
              </p>
              <div className="flex flex-col gap-8">
                <div className="flex gap-4">
                  <Dropdown
                    menuFullWidth
                    items={PERIOD_TYPE_OPTIONS}
                    error={!!errors.durationType}
                    errorMessage={errors.durationType ?? ''}
                    trigger={({ open, toggle, disabled, readOnly, error }) => (
                      <DropDownButton
                        label="Typ okresu"
                        value={durationType}
                        open={open}
                        toggle={toggle}
                        disabled={disabled}
                        readOnly={readOnly ?? false}
                        error={error}
                        hideChevron={readOnly ?? false}
                      />
                    )}
                    onItemClick={(id) => {
                      if (isSubmitting) {
                        return;
                      }
                      if (errors.durationType) {
                        setErrors((prev) => ({
                          ...prev,
                          durationType: '',
                        }));
                      }
                      setDurationType(id);
                    }}
                    disabled={isSubmitting}
                    readOnly={isPreviewStep}
                  />
                  <Dropdown
                    key={durationType || 'empty'}
                    menuFullWidth
                    items={
                      durationType === 'Miesięczny'
                        ? PERIOD_OPTIONS_MONTHLY
                        : PERIOD_OPTIONS_YEARLY
                    }
                    error={!!errors.period}
                    errorMessage={errors.period ?? ''}
                    trigger={({ open, toggle, disabled, readOnly, error }) => (
                      <DropDownButton
                        label="Okres"
                        value={period}
                        open={open}
                        toggle={toggle}
                        disabled={disabled}
                        readOnly={readOnly ?? false}
                        error={error}
                        hideChevron={readOnly ?? false}
                      />
                    )}
                    onItemClick={(id) => {
                      if (isSubmitting) {
                        return;
                      }
                      if (errors.period) {
                        setErrors((prev) => ({
                          ...prev,
                          period: '',
                        }));
                      }
                      setPeriod(id);
                    }}
                    disabled={durationType.length === 0 || isSubmitting}
                    readOnly={isPreviewStep}
                  />
                  {cycleOptions.length === 1 ? (
                    <TextField
                      label="Cykl rozliczeniowy"
                      value={cycle}
                      fullWidth={true}
                      disabled={isSubmitting}
                    />
                  ) : (
                    <Dropdown
                      key={`${durationType}-${period}`}
                      menuFullWidth
                      items={cycleOptions}
                      error={!!errors.cycle}
                      errorMessage={errors.cycle ?? ''}
                      trigger={({
                        open,
                        toggle,
                        disabled,
                        readOnly,
                        error,
                      }) => (
                        <DropDownButton
                          label="Cykl rozliczeniowy"
                          value={cycle}
                          open={open}
                          toggle={toggle}
                          disabled={disabled}
                          readOnly={readOnly ?? false}
                          error={error}
                          hideChevron={readOnly ?? false}
                        />
                      )}
                      onItemClick={(id) => {
                        if (isSubmitting) {
                          return;
                        }
                        if (errors.cycle) {
                          setErrors((prev) => ({
                            ...prev,
                            cycle: '',
                          }));
                        }
                        setCycle(id);
                      }}
                      disabled={
                        durationType.length === 0 ||
                        period.length === 0 ||
                        isSubmitting
                      }
                      readOnly={isPreviewStep}
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="border-b-2 border-[#DCDDE0]" />

            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">Cennik</p>
              <div className="flex flex-col gap-8">
                <div className="flex gap-4">
                  <div className="flex flex-col gap-2 w-[32%]">
                    <TextField
                      fullWidth={true}
                      label="Cena"
                      maxLength={200}
                      type="amount"
                      value={formatPriceWithSpaces(formData.price)}
                      onChange={(e) => {
                        if (isSubmitting) {
                          return;
                        }
                        if (errors.price) {
                          setErrors((prev) => ({
                            ...prev,
                            price: '',
                          }));
                        }
                        const raw = e.target.value.replace(/\s/g, '');
                        const clamped = clampPriceToMax(raw);
                        setFormData((prev) => ({
                          ...prev,
                          price: clamped === raw ? raw : clamped,
                        }));
                      }}
                      error={!!errors.price}
                      errorMessage={errors.price ?? ''}
                      disabled={isSubmitting}
                      readOnly={isPreviewStep}
                      trailingIcon={
                        formData.price ? (
                          <span className="text-bodyLarge text-neutral-10 align-middle ml-[2px] mt-[12px]">
                            zł
                          </span>
                        ) : null
                      }
                    />
                    <p className="text-bodySmall text-on-surface-variant ml-[2px]">
                      {getBillingDescription(
                        formData.price,
                        cycle,
                        period,
                        formatPriceWithSpaces
                      )}
                    </p>
                  </div>
                  <div className="flex flex-col gap-2 w-[33%]">
                    <OfferInfiniteDropdown
                      value={selectedOffer}
                      onChange={setSelectedOffer}
                      disabled={isPreviewStep || isSubmitting}
                      hideChevron={isPreviewStep}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="border-b-2 border-[#DCDDE0]" />

            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">
                Limity użytkowania
              </p>
              <div className="flex flex-col gap-8">
                <div className="flex gap-4">
                  <div className="flex flex-col gap-2 w-full">
                    <TextField
                      label="Liczba użytkowników"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={3}
                      fullWidth
                      value={formData.userCount}
                      onChange={(e) => {
                        if (isSubmitting) {
                          return;
                        }
                        if (errors.userCount) {
                          setErrors((prev) => ({
                            ...prev,
                            userCount: '',
                          }));
                        }
                        const digitsOnly = e.target.value.replace(/\D/g, '');

                        if (digitsOnly === '') {
                          setFormData((prev) => ({ ...prev, userCount: '' }));
                          return;
                        }

                        const num = parseInt(digitsOnly, 10);
                        const clamped = Math.min(500, Math.max(0, num));

                        setFormData((prev) => ({
                          ...prev,
                          userCount: String(clamped),
                        }));
                      }}
                      error={!!errors.userCount}
                      errorMessage={errors.userCount ?? ''}
                      disabled={isSubmitting}
                      readOnly={isPreviewStep}
                    />

                    {!errors.userCount && (
                      <p className="text-bodySmall text-on-surface-variant ml-[2px]">
                        Maximum allowed: 500 users
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 w-full">
                    <TextField
                      label="Liczba placówek"
                      maxLength={2}
                      fullWidth
                      type="text"
                      inputMode="numeric"
                      value={formData.facilityCount}
                      onChange={(e) => {
                        if (isSubmitting) {
                          return;
                        }
                        if (errors.facilityCount) {
                          setErrors((prev) => ({
                            ...prev,
                            facilityCount: '',
                          }));
                        }
                        const digitsOnly = e.target.value.replace(/\D/g, '');

                        if (digitsOnly === '') {
                          setFormData((prev) => ({
                            ...prev,
                            facilityCount: '',
                          }));
                          return;
                        }

                        const num = parseInt(digitsOnly, 10);
                        const clamped = Math.min(10, Math.max(0, num));

                        setFormData((prev) => ({
                          ...prev,
                          facilityCount: String(clamped),
                        }));
                      }}
                      error={!!errors.facilityCount}
                      errorMessage={errors.facilityCount ?? ''}
                      disabled={isSubmitting}
                      readOnly={isPreviewStep}
                    />

                    {!errors.facilityCount && (
                      <p className="text-bodySmall text-on-surface-variant ml-[2px]">
                        Maximum allowed: 10 facilities
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 w-full">
                    <TextField
                      label={
                        <>
                          Liczba dokumentów QMS{' '}
                          <span style={{ fontSize: 12 }}>(Optional)</span>
                        </>
                      }
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={4}
                      fullWidth={true}
                      value={formData.qmsDocCount}
                      onChange={(e) => {
                        if (isSubmitting) {
                          return;
                        }
                        const digitsOnly = e.target.value.replace(/\D/g, '');
                        if (digitsOnly === '') {
                          setFormData((prev) => ({
                            ...prev,
                            qmsDocCount: '',
                          }));
                          return;
                        }
                        const num = parseInt(digitsOnly, 10);
                        const clamped = Math.min(2000, Math.max(0, num));
                        setFormData((prev) => ({
                          ...prev,
                          qmsDocCount: String(clamped),
                        }));
                      }}
                      disabled={isSubmitting}
                      readOnly={isPreviewStep}
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      },
    []
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center bg-white h-screen w-full">
        <Loader />
      </div>
    );
  }

  return (
    <>
      <CreateLayout
        isSubmitting={isSubmitting}
        handleSubmit={handleSubmit}
        handleCancel={handleCancel}
        isCancelModalOpen={isCancelModalOpen}
        handleCloseCancelModal={handleCloseCancelModal}
        handleConfirmCancel={handleConfirmCancel}
        isSnackbarOpen={isSnackbarOpen}
        handleCloseSnackbar={handleCloseSnackbar}
        snackbarError={snackbarError}
        Header={EditSubscriptionHeader}
        cancelButtonLabel={isPreviewStep ? 'Wróć do edycji' : 'Anuluj'}
        submitButtonLabel={isPreviewStep ? 'Zapisz' : 'Przejdź do podglądu'}
        submitButtonLoadingLabel="Zapisywanie..."
        submitButtonDisabled={isSubmitting || !hasChanges}
      >
        <EditSubscriptionForm
          formData={formData}
          setFormData={setFormData}
          durationType={durationType}
          setDurationType={setDurationType}
          period={period}
          setPeriod={setPeriod}
          cycle={cycle}
          setCycle={setCycle}
          cycleOptions={cycleOptions}
          errors={errors}
          setErrors={setErrors}
          formatPriceWithSpaces={formatPriceWithSpaces}
          clampPriceToMax={clampPriceToMax}
          maxPriceFormatted={formatPriceWithSpaces(String(MAX_PRICE))}
          getBillingDescription={getBillingDescription}
          isSubmitting={isSubmitting}
          isPreviewStep={isPreviewStep}
          modifiedBy={modifiedBy}
          modifiedDate={modifiedDate}
          selectedOffer={selectedOffer}
          setSelectedOffer={setSelectedOffer}
        />
      </CreateLayout>

      {/* Status Update Dialog */}
      <UpdateSubscriptionStatusDialog
        open={isStatusDialogOpen}
        onClose={handleCloseStatusDialog}
        planName={formData.planName}
        isActive={status === 'active'}
        onConfirm={handleConfirmStatusUpdate}
        isSubmitting={isStatusSubmitting}
      />

      {/* Status Update Success Snackbar */}
      <Snackbar
        open={showStatusSuccessSnackbar}
        message={statusSuccessMessage}
        variant="success"
        onClose={handleCloseStatusSuccessSnackbar}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton={true}
      />

      {/* Status Update Error Snackbar */}
      <Snackbar
        open={showStatusErrorSnackbar}
        message={statusErrorMessage}
        variant="error"
        onClose={handleCloseStatusErrorSnackbar}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton={true}
      />
    </>
  );
};

export default EditSubscription;
